import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopNav from '../components/TopNav.jsx';
import { SpinnerOverlay } from '../components/index.jsx';
import { getExamsAdmin, getExamAdmin, createExam, updateExam, deleteExam, addWritingQuestion, updateWritingQuestion, deleteWritingQuestion, addSpeakingPart, updateSpeakingPart, deleteSpeakingPart, updateReadingPart, importGeneralWritingExam } from '../api/exams';
import { getLandingAdmin, getPromotionAdmin, saveLandingAdmin, savePromotionAdmin } from '../api/auth';
import { useAuth } from '../context/AuthContext';

const toDateTimeLocal = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const offsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
};

export default function Admin() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [exams, setExams] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [exam, setExam] = useState(null);
  const [adminSection, setAdminSection] = useState('exams');
  const [tab, setTab] = useState('writing');
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // {type, data}
  const [jsonText, setJsonText] = useState('');
  const [importStatus, setImportStatus] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null); // ID of exam to delete
  const [planPackages, setPlanPackages] = useState([]);
  const [packageSaving, setPackageSaving] = useState({});
  const [packageSaved, setPackageSaved] = useState({});
  const [packageMessages, setPackageMessages] = useState({});
  const [landingSettings, setLandingSettings] = useState([]);
  const [landingSaving, setLandingSaving] = useState({});
  const [landingSaved, setLandingSaved] = useState({});
  const [landingMessages, setLandingMessages] = useState({});

  useEffect(() => {
    getExamsAdmin({ family: 'general' }).then(data => { setExams(data); if (data[0]) setSelectedId(data[0].id); }).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (selectedId) getExamAdmin(selectedId).then(setExam).catch(console.error);
  }, [selectedId]);

  useEffect(() => {
    if (!user?.is_admin) return;
    getPromotionAdmin()
      .then((data) => {
        const packages = (data.packages || []).map((item) => ({
          ...item,
          expires_at: toDateTimeLocal(item.expires_at),
        }));
        setPlanPackages(packages);
        setPackageSaved(Object.fromEntries(packages.map((item) => [item.plan, true])));
      })
      .catch(console.error);
    getLandingAdmin()
      .then((data) => {
        const items = (data.settings || []).map((item) => ({
          ...item,
          countdown_target: toDateTimeLocal(item.countdown_target),
        }));
        setLandingSettings(items);
        setLandingSaved(Object.fromEntries(items.map((item) => [item.app_key, true])));
      })
      .catch(console.error);
  }, [user]);

  const refresh = () => getExamAdmin(selectedId).then(setExam);
  const refreshList = () => getExamsAdmin({ family: 'general' }).then(setExams);
  const importWritingJson = async () => {
    try {
      setImportStatus('');
      const payload = JSON.parse(jsonText);
      const imported = await importGeneralWritingExam(selectedId ? { exam_id: selectedId, payload } : { payload });
      await refreshList();
      setSelectedId(imported.id);
      const detail = await getExamAdmin(imported.id);
      setExam(detail);
      setImportStatus('Writing JSON imported successfully.');
    } catch (err) {
      console.error(err);
      const backendError = err?.response?.data?.error || err?.response?.data?.detail || err?.message || 'Could not import the JSON file.';
      setImportStatus(backendError);
    }
  };
  const onUploadJsonFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setJsonText(text);
  };
  const setPackageField = (plan, key, value) => {
    setPackageSaved((current) => ({ ...current, [plan]: false }));
    setPackageMessages((current) => ({ ...current, [plan]: '' }));
    setPlanPackages((current) => current.map((item) => item.plan === plan ? { ...item, [key]: value } : item));
  };
  const savePackage = async (pkg) => {
    setPackageSaving((current) => ({ ...current, [pkg.plan]: true }));
    setPackageMessages((current) => ({ ...current, [pkg.plan]: '' }));
    try {
      const result = await savePromotionAdmin({
        kind: pkg.kind,
        code: pkg.code,
        plan: pkg.plan,
        price: pkg.price,
        included_credits: pkg.included_credits,
        is_active: pkg.is_active,
        expires_at: pkg.plan === 'promo' && pkg.expires_at ? new Date(pkg.expires_at).toISOString() : null,
        title_ar: pkg.content?.title?.ar || '',
        title_en: pkg.content?.title?.en || '',
        subtitle_ar: pkg.content?.subtitle?.ar || '',
        subtitle_en: pkg.content?.subtitle?.en || '',
        hook_ar: pkg.content?.hook?.ar || '',
        hook_en: pkg.content?.hook?.en || '',
        description_ar: pkg.content?.description?.ar || '',
        description_en: pkg.content?.description?.en || '',
        cta_ar: pkg.content?.cta?.ar || '',
        cta_en: pkg.content?.cta?.en || '',
        badge_ar: pkg.content?.badge?.ar || '',
        badge_en: pkg.content?.badge?.en || '',
        features_ar: pkg.content?.features?.ar || [],
        features_en: pkg.content?.features?.en || [],
      });
      setPlanPackages((current) => current.map((item) => item.plan === pkg.plan ? {
        ...result.package,
        expires_at: toDateTimeLocal(result.package.expires_at),
      } : item));
      setPackageSaved((current) => ({ ...current, [pkg.plan]: true }));
      setPackageMessages((current) => ({ ...current, [pkg.plan]: result.message || 'Package saved successfully.' }));
    } catch (err) {
      setPackageMessages((current) => ({ ...current, [pkg.plan]: err?.response?.data?.error || 'Could not save package right now.' }));
    } finally {
      setPackageSaving((current) => ({ ...current, [pkg.plan]: false }));
    }
  };
  const setLandingField = (appKey, key, value) => {
    setLandingSaved((current) => ({ ...current, [appKey]: false }));
    setLandingMessages((current) => ({ ...current, [appKey]: '' }));
    setLandingSettings((current) => current.map((item) => item.app_key === appKey ? { ...item, [key]: value } : item));
  };
  const saveLanding = async (setting) => {
    setLandingSaving((current) => ({ ...current, [setting.app_key]: true }));
    setLandingMessages((current) => ({ ...current, [setting.app_key]: '' }));
    try {
      const result = await saveLandingAdmin({
        app_key: setting.app_key,
        countdown_enabled: setting.countdown_enabled,
        countdown_target: setting.countdown_target ? new Date(setting.countdown_target).toISOString() : null,
        hero_badge_ar: setting.hero_badge?.ar || '',
        hero_badge_en: setting.hero_badge?.en || '',
        hero_title_ar: setting.hero_title?.ar || '',
        hero_title_en: setting.hero_title?.en || '',
        hero_subtitle_ar: setting.hero_subtitle?.ar || '',
        hero_subtitle_en: setting.hero_subtitle?.en || '',
        primary_cta_ar: setting.primary_cta?.ar || '',
        primary_cta_en: setting.primary_cta?.en || '',
        secondary_cta_ar: setting.secondary_cta?.ar || '',
        secondary_cta_en: setting.secondary_cta?.en || '',
      });
      setLandingSettings((current) => current.map((item) => item.app_key === setting.app_key ? {
        ...result.setting,
        countdown_target: toDateTimeLocal(result.setting.countdown_target),
      } : item));
      setLandingSaved((current) => ({ ...current, [setting.app_key]: true }));
      setLandingMessages((current) => ({ ...current, [setting.app_key]: result.message || 'Landing settings saved successfully.' }));
    } catch (err) {
      setLandingMessages((current) => ({ ...current, [setting.app_key]: err?.response?.data?.error || 'Could not save landing settings right now.' }));
    } finally {
      setLandingSaving((current) => ({ ...current, [setting.app_key]: false }));
    }
  };

  const handleDeleteExam = async () => {
    if (!confirmDelete) return;
    try {
      await deleteExam(confirmDelete);
      await refreshList();
      if (selectedId === confirmDelete) setSelectedId(null);
      setExam(null);
      setConfirmDelete(null);
    } catch (err) {
      alert(err?.response?.data?.error || 'Could not delete exam.');
    }
  };

  const handleDeleteQuestion = async (qId) => {
    if (!window.confirm('Delete?')) return;
    await deleteWritingQuestion(selectedId, qId);
    refresh();
  };

  const handleDeleteSpeaking = async (pId) => {
    if (!window.confirm('Delete?')) return;
    await deleteSpeakingPart(selectedId, pId);
    refresh();
  };

  if (loading) return <><TopNav /><SpinnerOverlay /></>;

  const TYPE_ICONS = { email: '✉', article: '📰', story: '📖', picture: '🖼' };
  const TYPE_COLORS = { email: { bg: '#dbeafe', color: '#1e40af' }, article: { bg: '#dcfce7', color: '#15803d' }, story: { bg: '#fef3c7', color: '#92400e' }, picture: { bg: '#ede9fe', color: '#5b21b6' } };
  const PACKAGE_META = {
    free: {
      title: 'Free Credits',
      subtitle: 'One-time claimable credits',
      bg: 'linear-gradient(135deg,#7c3aed,#8b5cf6)',
      activeText: 'Claim Enabled',
      inactiveText: 'Claim Disabled',
      helper: 'This controls the free-credits card shown to eligible users.',
    },
    promo: {
      title: 'Promo Trial',
      subtitle: 'Marketing offer with expiry and limited credits',
      bg: 'linear-gradient(135deg,#dc2626,#f97316 60%,#f59e0b)',
      activeText: 'Promo Enabled',
      inactiveText: 'Promo Disabled',
      helper: 'When disabled, free/new users will only see Basic and AI packages.',
    },
    basic: {
      title: 'Basic Practice',
      subtitle: 'Manual practice plan without AI marking',
      bg: 'linear-gradient(135deg,#1d4ed8,#3b82f6)',
      activeText: 'Basic Visible',
      inactiveText: 'Basic Hidden',
      helper: 'Use this card to control the one-time Basic package price and availability.',
    },
    ai: {
      title: 'AI Practice',
      subtitle: 'Main revenue package with AI feedback and credits',
      bg: 'linear-gradient(135deg,#059669,#10b981)',
      activeText: 'AI Visible',
      inactiveText: 'AI Hidden',
      helper: 'Use this card to control the main AI package price and visibility.',
    },
    credits_5: {
      title: '5 AI Credits',
      subtitle: 'Credit pack',
      bg: 'linear-gradient(135deg,#0284c7,#2563eb)',
      activeText: 'Visible',
      inactiveText: 'Hidden',
      helper: 'Edit the bilingual text for this credit pack.',
    },
    credits_10: {
      title: '10 AI Credits',
      subtitle: 'Credit pack',
      bg: 'linear-gradient(135deg,#0369a1,#0ea5e9)',
      activeText: 'Visible',
      inactiveText: 'Hidden',
      helper: 'Edit the bilingual text for this credit pack.',
    },
    credits_25: {
      title: '25 AI Credits',
      subtitle: 'Credit pack',
      bg: 'linear-gradient(135deg,#0f766e,#14b8a6)',
      activeText: 'Visible',
      inactiveText: 'Hidden',
      helper: 'Edit the bilingual text for this credit pack.',
    },
  };

  return (
    <div>
      <TopNav
        backTo="/platform"
        backLabel="Platform"
        actions={exams.length === 0 ? <button className="btn btn-gold btn-sm" onClick={() => setModal({ type: 'new-exam' })}>+ New Exam</button> : null}
      />

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '20px 16px' }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
          {[
            ['exams', 'Exam Manager'],
            ['packages', 'Packages'],
            ['landing', 'Landing Pages'],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={`btn btn-sm ${adminSection === key ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setAdminSection(key)}
            >
              {label}
            </button>
          ))}
        </div>
        {user?.is_admin && adminSection === 'packages' && planPackages.length > 0 && (
          <div style={{ display: 'grid', gap: 16, marginBottom: 16 }}>
            {planPackages.map((pkg) => {
              const meta = PACKAGE_META[pkg.plan] || PACKAGE_META.promo;
              return (
                <div key={pkg.plan} className="card" style={{ overflow: 'hidden', border: '1px solid rgba(148,163,184,.18)', boxShadow: '0 18px 40px rgba(15,23,42,.08)' }}>
                  <div style={{ padding: '14px 18px', background: meta.bg, color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 800 }}>{meta.title}</div>
                      <div style={{ fontSize: 12, opacity: 0.88, marginTop: 2 }}>{meta.subtitle}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <span className={`badge ${pkg.is_active ? 'badge-green' : 'badge-red'}`}>{pkg.is_active ? meta.activeText : meta.inactiveText}</span>
                      <span className="badge badge-gold">Source: {pkg.source}</span>
                    </div>
                  </div>
                  <div style={{ padding: 18 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 14 }}>
                      <div className="fg">
                        <label className="fl">Package code</label>
                        <input className="fc" value={pkg.code} onChange={(e) => setPackageField(pkg.plan, 'code', e.target.value)} />
                      </div>
                      <div className="fg">
                        <label className="fl">Price (SAR)</label>
                        <input className="fc" type="number" step="0.01" value={pkg.price} onChange={(e) => setPackageField(pkg.plan, 'price', e.target.value)} disabled={pkg.kind === 'free'} />
                      </div>
                      <div className="fg">
                        <label className="fl">Included credits</label>
                        <input className="fc" type="number" min="0" value={pkg.included_credits ?? 0} onChange={(e) => setPackageField(pkg.plan, 'included_credits', e.target.value)} />
                      </div>
                      {pkg.plan === 'promo' ? (
                        <div className="fg">
                          <label className="fl">Promo expiry</label>
                          <input className="fc" type="datetime-local" value={pkg.expires_at || ''} onChange={(e) => setPackageField(pkg.plan, 'expires_at', e.target.value)} />
                        </div>
                      ) : (
                        <div className="fg">
                          <label className="fl">Expiry</label>
                          <input className="fc" value="No expiry for this package" disabled />
                        </div>
                      )}
                      <div className="fg" style={{ display: 'flex', alignItems: 'end' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, fontWeight: 700 }}>
                          <input type="checkbox" checked={pkg.is_active} onChange={(e) => setPackageField(pkg.plan, 'is_active', e.target.checked)} />
                          Package is active
                        </label>
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: 14, marginTop: 14 }}>
                      <div className="fg">
                        <label className="fl">Title Arabic</label>
                        <input className="fc" value={pkg.content?.title?.ar || ''} onChange={(e) => setPackageField(pkg.plan, 'content', { ...pkg.content, title: { ...pkg.content?.title, ar: e.target.value } })} />
                      </div>
                      <div className="fg">
                        <label className="fl">Title English</label>
                        <input className="fc" value={pkg.content?.title?.en || ''} onChange={(e) => setPackageField(pkg.plan, 'content', { ...pkg.content, title: { ...pkg.content?.title, en: e.target.value } })} />
                      </div>
                      <div className="fg">
                        <label className="fl">Subtitle Arabic</label>
                        <input className="fc" value={pkg.content?.subtitle?.ar || ''} onChange={(e) => setPackageField(pkg.plan, 'content', { ...pkg.content, subtitle: { ...pkg.content?.subtitle, ar: e.target.value } })} />
                      </div>
                      <div className="fg">
                        <label className="fl">Subtitle English</label>
                        <input className="fc" value={pkg.content?.subtitle?.en || ''} onChange={(e) => setPackageField(pkg.plan, 'content', { ...pkg.content, subtitle: { ...pkg.content?.subtitle, en: e.target.value } })} />
                      </div>
                      <div className="fg">
                        <label className="fl">CTA Arabic</label>
                        <input className="fc" value={pkg.content?.cta?.ar || ''} onChange={(e) => setPackageField(pkg.plan, 'content', { ...pkg.content, cta: { ...pkg.content?.cta, ar: e.target.value } })} />
                      </div>
                      <div className="fg">
                        <label className="fl">CTA English</label>
                        <input className="fc" value={pkg.content?.cta?.en || ''} onChange={(e) => setPackageField(pkg.plan, 'content', { ...pkg.content, cta: { ...pkg.content?.cta, en: e.target.value } })} />
                      </div>
                      <div className="fg">
                        <label className="fl">Hook Arabic</label>
                        <textarea className="fc" style={{ height: 88, padding: 12 }} value={pkg.content?.hook?.ar || ''} onChange={(e) => setPackageField(pkg.plan, 'content', { ...pkg.content, hook: { ...pkg.content?.hook, ar: e.target.value } })} />
                      </div>
                      <div className="fg">
                        <label className="fl">Hook English</label>
                        <textarea className="fc" style={{ height: 88, padding: 12 }} value={pkg.content?.hook?.en || ''} onChange={(e) => setPackageField(pkg.plan, 'content', { ...pkg.content, hook: { ...pkg.content?.hook, en: e.target.value } })} />
                      </div>
                      <div className="fg">
                        <label className="fl">Features Arabic</label>
                        <textarea className="fc" style={{ height: 110, padding: 12 }} value={(pkg.content?.features?.ar || []).join('\n')} onChange={(e) => setPackageField(pkg.plan, 'content', { ...pkg.content, features: { ...pkg.content?.features, ar: e.target.value.split('\n').map((item) => item.trim()).filter(Boolean) } })} />
                      </div>
                      <div className="fg">
                        <label className="fl">Features English</label>
                        <textarea className="fc" style={{ height: 110, padding: 12 }} value={(pkg.content?.features?.en || []).join('\n')} onChange={(e) => setPackageField(pkg.plan, 'content', { ...pkg.content, features: { ...pkg.content?.features, en: e.target.value.split('\n').map((item) => item.trim()).filter(Boolean) } })} />
                      </div>
                    </div>
                    <div style={{ marginTop: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                      <div style={{ fontSize: 13, color: packageMessages[pkg.plan] && packageSaved[pkg.plan] ? '#15803d' : '#64748b', fontWeight: packageMessages[pkg.plan] ? 700 : 500 }}>
                        {packageMessages[pkg.plan] || meta.helper}
                      </div>
                      {!packageSaved[pkg.plan] ? (
                        <button className="btn btn-gold btn-sm" onClick={() => savePackage(pkg)} disabled={!!packageSaving[pkg.plan]}>
                          {packageSaving[pkg.plan] ? 'Saving…' : 'Save Package'}
                        </button>
                      ) : (
                        <span className="badge badge-green">Saved ✓</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {user?.is_admin && adminSection === 'landing' && landingSettings.length > 0 && (
          <div style={{ display: 'grid', gap: 16, marginBottom: 16 }}>
            {landingSettings.map((setting) => (
              <div key={setting.app_key} className="card" style={{ overflow: 'hidden', border: '1px solid rgba(148,163,184,.18)', boxShadow: '0 18px 40px rgba(15,23,42,.08)' }}>
                <div style={{ padding: '14px 18px', background: setting.app_key === 'fet' ? 'linear-gradient(135deg,#312e81,#4f46e5)' : 'linear-gradient(135deg,#0f172a,#2563eb)', color: '#fff' }}>
                  <div style={{ fontSize: 16, fontWeight: 800 }}>{setting.app_key === 'fet' ? 'FET Landing Page' : 'ITS Landing Page'}</div>
                  <div style={{ fontSize: 12, opacity: 0.88, marginTop: 2 }}>Countdown and hero content can be managed here.</div>
                </div>
                <div style={{ padding: 18 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 14 }}>
                    <div className="fg">
                      <label className="fl">Countdown target</label>
                      <input className="fc" type="datetime-local" value={setting.countdown_target || ''} onChange={(e) => setLandingField(setting.app_key, 'countdown_target', e.target.value)} />
                    </div>
                    <div className="fg" style={{ display: 'flex', alignItems: 'end' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, fontWeight: 700 }}>
                        <input type="checkbox" checked={!!setting.countdown_enabled} onChange={(e) => setLandingField(setting.app_key, 'countdown_enabled', e.target.checked)} />
                        Countdown enabled
                      </label>
                    </div>
                    <div className="fg">
                      <label className="fl">Hero badge Arabic</label>
                      <input className="fc" value={setting.hero_badge?.ar || ''} onChange={(e) => setLandingField(setting.app_key, 'hero_badge', { ...setting.hero_badge, ar: e.target.value })} />
                    </div>
                    <div className="fg">
                      <label className="fl">Hero badge English</label>
                      <input className="fc" value={setting.hero_badge?.en || ''} onChange={(e) => setLandingField(setting.app_key, 'hero_badge', { ...setting.hero_badge, en: e.target.value })} />
                    </div>
                    <div className="fg">
                      <label className="fl">Hero title Arabic</label>
                      <input className="fc" value={setting.hero_title?.ar || ''} onChange={(e) => setLandingField(setting.app_key, 'hero_title', { ...setting.hero_title, ar: e.target.value })} />
                    </div>
                    <div className="fg">
                      <label className="fl">Hero title English</label>
                      <input className="fc" value={setting.hero_title?.en || ''} onChange={(e) => setLandingField(setting.app_key, 'hero_title', { ...setting.hero_title, en: e.target.value })} />
                    </div>
                    <div className="fg">
                      <label className="fl">Primary CTA Arabic</label>
                      <input className="fc" value={setting.primary_cta?.ar || ''} onChange={(e) => setLandingField(setting.app_key, 'primary_cta', { ...setting.primary_cta, ar: e.target.value })} />
                    </div>
                    <div className="fg">
                      <label className="fl">Primary CTA English</label>
                      <input className="fc" value={setting.primary_cta?.en || ''} onChange={(e) => setLandingField(setting.app_key, 'primary_cta', { ...setting.primary_cta, en: e.target.value })} />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: 14, marginTop: 14 }}>
                    <div className="fg">
                      <label className="fl">Hero subtitle Arabic</label>
                      <textarea className="fc" style={{ height: 96, padding: 12 }} value={setting.hero_subtitle?.ar || ''} onChange={(e) => setLandingField(setting.app_key, 'hero_subtitle', { ...setting.hero_subtitle, ar: e.target.value })} />
                    </div>
                    <div className="fg">
                      <label className="fl">Hero subtitle English</label>
                      <textarea className="fc" style={{ height: 96, padding: 12 }} value={setting.hero_subtitle?.en || ''} onChange={(e) => setLandingField(setting.app_key, 'hero_subtitle', { ...setting.hero_subtitle, en: e.target.value })} />
                    </div>
                    <div className="fg">
                      <label className="fl">Secondary CTA Arabic</label>
                      <input className="fc" value={setting.secondary_cta?.ar || ''} onChange={(e) => setLandingField(setting.app_key, 'secondary_cta', { ...setting.secondary_cta, ar: e.target.value })} />
                    </div>
                    <div className="fg">
                      <label className="fl">Secondary CTA English</label>
                      <input className="fc" value={setting.secondary_cta?.en || ''} onChange={(e) => setLandingField(setting.app_key, 'secondary_cta', { ...setting.secondary_cta, en: e.target.value })} />
                    </div>
                  </div>
                  <div style={{ marginTop: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                    <div style={{ fontSize: 13, color: landingMessages[setting.app_key] && landingSaved[setting.app_key] ? '#15803d' : '#64748b', fontWeight: landingMessages[setting.app_key] ? 700 : 500 }}>
                      {landingMessages[setting.app_key] || 'These settings control the first screen users see.'}
                    </div>
                    {!landingSaved[setting.app_key] ? (
                      <button className="btn btn-gold btn-sm" onClick={() => saveLanding(setting)} disabled={!!landingSaving[setting.app_key]}>
                        {landingSaving[setting.app_key] ? 'Saving…' : 'Save Landing Settings'}
                      </button>
                    ) : (
                      <span className="badge badge-green">Saved ✓</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {adminSection === 'exams' && (
        <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 16 }}>

          {/* Sidebar */}
          <div className="card">
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--bo)', fontWeight: 600, fontSize: 13, background: 'var(--nv)', color: '#fff', borderRadius: 'var(--r-lg) var(--r-lg) 0 0' }}>
              Exams ({exams.length})
            </div>
            {exams.length === 0
              ? <div style={{ padding: 16, textAlign: 'center', color: 'var(--mu)', fontSize: 13 }}>No exams yet.</div>
              : exams.map(e => (
                <div key={e.id} className={`eli${selectedId === e.id ? ' on' : ''}`} onClick={() => setSelectedId(e.id)}>
                  <div>
                    <div className="eli-t" style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span>{e.title}</span>
                      <span className={`badge ${e.is_active ? 'badge-green' : 'badge-red'}`}>{e.is_active ? 'Active' : 'Inactive'}</span>
                      <span className={`badge ${e.is_complete ? 'badge-teal' : 'badge-gold'}`}>{e.is_complete ? 'Complete' : 'Incomplete'}</span>
                    </div>
                    <div className="eli-m">{e.question_count || 0} q · {e.created_at?.split('T')[0]}</div>
                    {!e.is_complete && <div className="eli-m" style={{ color: '#b45309', marginTop: 4 }}>{e.activation_block_reason}</div>}
                  </div>
                  <button className="BI D" onClick={ev => { ev.stopPropagation(); setConfirmDelete(e.id); }}>🗑</button>
                </div>
              ))
            }
          </div>

          {/* Detail */}
          <div>
            {!exam ? (
              <div className="card card-body" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 260, color: 'var(--mu)', gap: 10 }}>
                <div style={{ fontSize: 36, opacity: .2 }}>📋</div>
                <p style={{ fontSize: 14 }}>Select an exam to manage</p>
              </div>
            ) : (
              <div className="card">
                <div style={{ padding: '13px 18px', borderBottom: '1px solid var(--bo)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600 }}>{exam.title}</div>
                    <div style={{ fontSize: 11, color: 'var(--mu)', marginTop: 2 }}>{exam.description} · {exam.time_mins} min</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                      <span className={`badge ${exam.is_active ? 'badge-green' : 'badge-red'}`}>{exam.is_active ? 'Exam is live' : 'Exam is hidden'}</span>
                      <span className={`badge ${exam.is_complete ? 'badge-teal' : 'badge-gold'}`}>{exam.is_complete ? 'Ready for activation' : 'Needs content'}</span>
                    </div>
                    {!exam.is_complete && (
                      <div style={{ fontSize: 12, color: '#b45309', marginTop: 8, fontWeight: 600 }}>
                        {exam.activation_block_reason}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    <button 
                      className={`btn ${exam.is_active ? 'btn-ghost' : 'btn-teal'} btn-sm`} 
                      style={{ border: exam.is_active ? '1px solid #fca5a5' : '', color: exam.is_active ? '#b91c1c' : '' }}
                      onClick={async () => {
                        try {
                          await updateExam(exam.id, { is_active: !exam.is_active });
                          await refresh();
                          await refreshList();
                        } catch (err) {
                          alert(err?.response?.data?.error || 'Could not update status');
                        }
                      }}
                    >
                      {exam.is_active ? 'Unpublish Exam' : 'Publish Exam'}
                    </button>
                    <button className="btn btn-ghost btn-sm" onClick={() => setModal({ type: 'edit-exam', data: exam })}>✏ Edit</button>
                    <button className="btn btn-primary btn-sm" onClick={() => navigate(`/hub/${exam.id}`)}>Preview ↗</button>
                  </div>
                </div>

                {/* Tabs */}
                <div className="tab-bar">
                  {[['writing', '✍ Writing'], ['speaking', '🎙 Speaking'], ['reading', '📖 Reading']].map(([t, l]) => (
                    <button key={t} className={`tab-btn${tab === t ? ' active' : ''}`} onClick={() => setTab(t)}>{l}</button>
                  ))}
                </div>

                {/* Writing tab */}
                {tab === 'writing' && (
                  <>
                    <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--bo)', background: 'var(--li)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 12, color: 'var(--mu)' }}>Writing questions for AI marking</span>
                      <button className="btn btn-primary btn-sm" onClick={() => setModal({ type: 'add-question', data: { exam } })}>+ Add Question</button>
                    </div>
                    <div style={{ padding: '16px', borderBottom: '1px solid var(--bo)', background: '#f8fafc' }}>
                      <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>Import Writing JSON</div>
                      <div style={{ fontSize: 13, color: 'var(--mu)', marginBottom: 12 }}>
                        Create a new general exam or replace only the writing questions of the selected exam from JSON.
                      </div>
                      {importStatus && (
                        <div style={{ marginBottom: 12, padding: '10px 12px', borderRadius: 10, border: '1px solid var(--bo)', background: '#fff', color: importStatus.toLowerCase().includes('success') ? '#166534' : '#b91c1c', fontSize: 13 }}>
                          {importStatus}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                        <label className="btn btn-ghost btn-sm" style={{ cursor: 'pointer' }}>
                          Upload JSON
                          <input type="file" accept="application/json" onChange={onUploadJsonFile} style={{ display: 'none' }} />
                        </label>
                        <button className="btn btn-primary btn-sm" onClick={importWritingJson} disabled={!jsonText.trim()}>
                          {selectedId ? 'Replace Writing from JSON' : 'Create Exam from JSON'}
                        </button>
                        <a className="btn btn-ghost btn-sm" href="/general-writing-sample.json" download>
                          Download Sample
                        </a>
                      </div>
                      <textarea
                        className="fc fcs"
                        rows={10}
                        value={jsonText}
                        onChange={(e) => setJsonText(e.target.value)}
                        placeholder="Paste writing-only general exam JSON here..."
                        style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13 }}
                      />
                    </div>
                    {(exam.questions || []).length === 0
                      ? <div style={{ padding: 16, textAlign: 'center', color: 'var(--mu)', fontSize: 13 }}>No writing questions yet.</div>
                      : (exam.questions || []).map(q => (
                        <div key={q.id} className="q-row">
                          <div className="q-type-icon" style={{ background: TYPE_COLORS[q.question_type]?.bg, color: TYPE_COLORS[q.question_type]?.color }}>{TYPE_ICONS[q.question_type]}</div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13, fontWeight: 500 }}>{q.label} <span className={`badge ${q.required ? 'badge-red' : 'badge-gray'}`}>{q.required ? 'Required' : 'Optional'}</span></div>
                            <div style={{ fontSize: 11, color: 'var(--mu)', marginTop: 2 }}>Part {q.part} · {q.question_type}</div>
                          </div>
                          <div style={{ display: 'flex', gap: 3 }}>
                            <button className="btn btn-ghost btn-sm" onClick={() => setModal({ type: 'edit-question', data: q })}>Edit</button>
                            <button className="btn btn-red btn-sm" onClick={() => handleDeleteQuestion(q.id)}>Del</button>
                          </div>
                        </div>
                      ))
                    }
                  </>
                )}

                {/* Speaking tab */}
                {tab === 'speaking' && (
                  <>
                    <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--bo)', background: 'var(--li)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 12, color: 'var(--mu)' }}>Speaking parts the AI examiner will use</span>
                      <button className="btn btn-teal btn-sm" onClick={() => setModal({ type: 'add-speaking' })}>+ Add Part</button>
                    </div>
                    {(exam.speaking_parts || []).length === 0
                      ? <div style={{ padding: 16, textAlign: 'center', color: 'var(--mu)', fontSize: 13 }}>No speaking parts yet.</div>
                      : (exam.speaking_parts || []).map(p => (
                        <div key={p.id} className="q-row">
                          <div className="q-type-icon" style={{ background: '#d1fae5', color: 'var(--tl)' }}>🎙</div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13, fontWeight: 500 }}>{p.label} <span className="badge badge-teal">Part {p.part}</span></div>
                            <div style={{ fontSize: 11, color: 'var(--mu)', marginTop: 2 }}>{(p.questions || []).length} questions</div>
                          </div>
                          <div style={{ display: 'flex', gap: 3 }}>
                            <button className="btn btn-ghost btn-sm" onClick={() => setModal({ type: 'edit-speaking', data: p })}>Edit</button>
                            <button className="btn btn-red btn-sm" onClick={() => handleDeleteSpeaking(p.id)}>Del</button>
                          </div>
                        </div>
                      ))
                    }
                  </>
                )}

                {/* Reading tab */}
                {tab === 'reading' && (
                  <>
                    <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--bo)', background: 'var(--li)' }}>
                      <span style={{ fontSize: 12, color: 'var(--mu)' }}>Upload reading passages and questions for each of the 6 parts</span>
                    </div>
                    {(exam.reading_parts || []).map(p => (
                      <div key={p.id} className="q-row">
                        <div className="q-type-icon" style={{ background: p.has_content ? '#ede9fe' : 'var(--li)', color: p.has_content ? 'var(--pu)' : 'var(--mu)' }}>📖</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 500 }}>Part {p.part_number} — {p.type_name}</div>
                          <div style={{ fontSize: 11, color: 'var(--mu)', marginTop: 2 }}>{p.question_count || 0} questions</div>
                        </div>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <span className={`badge ${p.has_content ? 'badge-green' : 'badge-gold'}`}>{p.has_content ? '✓ Ready' : 'No content'}</span>
                          <button className={`btn ${p.has_content ? 'btn-ghost' : 'btn-purple'} btn-sm`} onClick={() => setModal({ type: 'edit-reading', data: p })}>
                            {p.has_content ? '✏ Edit' : '+ Add'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        )}
      </div>

      {/* Modals */}
      {modal && (
        <AdminModal
          modal={modal}
          exam={exam}
          onClose={() => setModal(null)}
          onSaved={async () => { await refresh(); await refreshList(); setModal(null); }}
          onNewExam={async () => { await refreshList(); setModal(null); }}
        />
      )}

      {confirmDelete && (
        <div className="modal-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="modal-box" style={{ maxWidth: 400, padding: 0 }} onClick={e => e.stopPropagation()}>
            <div className="modal-head" style={{ borderBottom: 'none', paddingBottom: 0 }}>
              <h3 style={{ color: '#b91c1c' }}>Delete Exam</h3>
            </div>
            <div className="modal-body" style={{ padding: '16px 20px', color: 'var(--mu)', fontSize: 14 }}>
              Are you sure you want to permanently delete this exam? This action cannot be undone.
            </div>
            <div className="modal-foot" style={{ background: '#f8fafc', borderRadius: '0 0 22px 22px' }}>
              <button className="btn btn-ghost" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button className="btn btn-red" onClick={handleDeleteExam}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AdminModal({ modal, exam, onClose, onSaved, onNewExam }) {
  const [form, setForm] = useState(modal.data || {});
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const parseTimeMinutes = () => {
    const raw = `${form.time_mins ?? ''}`.trim();
    if (!raw) return 45;
    const parsed = parseInt(raw, 10);
    return Number.isNaN(parsed) ? 45 : parsed;
  };

  const save = async () => {
    setSaving(true);
    try {
      if (modal.type === 'new-exam') {
        await createExam({ title: form.title, description: form.description, time_mins: parseTimeMinutes(), is_active: !!form.is_active });
        onNewExam();
      } else if (modal.type === 'edit-exam') {
        await updateExam(exam.id, { title: form.title, description: form.description, time_mins: parseTimeMinutes(), is_active: !!form.is_active });
        onSaved();
      } else if (modal.type === 'add-question') {
        await addWritingQuestion(exam.id, { ...form, exam: exam.id });
        onSaved();
      } else if (modal.type === 'edit-question') {
        await updateWritingQuestion(exam.id, form.id, form);
        onSaved();
      } else if (modal.type === 'add-speaking') {
        await addSpeakingPart(exam.id, form);
        onSaved();
      } else if (modal.type === 'edit-speaking') {
        await updateSpeakingPart(exam.id, form.id, form);
        onSaved();
      } else if (modal.type === 'edit-reading') {
        let content = {};
        try { content = JSON.parse(form.content_json || '{}'); } catch { alert('Invalid JSON'); setSaving(false); return; }
        await updateReadingPart(exam.id, form.part_number, content);
        onSaved();
      }
    } catch (err) {
      console.error(err);
      const backendError = err?.response?.data?.error || err?.response?.data?.detail || err?.message || 'Save failed.';
      alert('Save failed: ' + backendError);
    }
    finally { setSaving(false); }
  };

  const titles = { 'new-exam': 'New Exam', 'edit-exam': 'Edit Exam', 'add-question': 'Add Writing Question', 'edit-question': 'Edit Question', 'add-speaking': 'Add Speaking Part', 'edit-speaking': 'Edit Speaking Part', 'edit-reading': `Edit Reading Part ${form.part_number}` };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box">
        <div className="modal-head"><h3>{titles[modal.type]}</h3><button className="modal-close" onClick={onClose}>×</button></div>
        <div className="modal-body">
          {(modal.type === 'new-exam' || modal.type === 'edit-exam') && (
            <>
              <div className="fg"><label className="fl">Title</label><input className="fc" value={form.title || ''} onChange={e => set('title', e.target.value)} /></div>
              <div className="fg"><label className="fl">Description</label><input className="fc" value={form.description || ''} onChange={e => set('description', e.target.value)} /></div>
              <div className="fg"><label className="fl">Time (minutes)</label><input className="fc" type="number" value={form.time_mins ?? ''} onChange={e => set('time_mins', e.target.value)} placeholder="45" /></div>
              <div className="fg">
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, fontWeight: 700 }}>
                  <input type="checkbox" checked={!!form.is_active} onChange={e => set('is_active', e.target.checked)} />
                  Make exam active
                </label>
                <div style={{ fontSize: 12, color: 'var(--mu)' }}>
                  Incomplete exams stay inactive until at least one usable question or content section is added.
                </div>
                {modal.type === 'edit-exam' && form.activation_block_reason && !form.is_complete && (
                  <div style={{ fontSize: 12, color: '#b45309', fontWeight: 700 }}>
                    {form.activation_block_reason}
                  </div>
                )}
              </div>
            </>
          )}
          {(modal.type === 'add-question' || modal.type === 'edit-question') && (
            <>
              <div className="fg"><label className="fl">Type</label>
                <select className="fc" value={form.question_type || 'email'} onChange={e => set('question_type', e.target.value)}>
                  {['email', 'article', 'story', 'picture'].map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="fg"><label className="fl">Label</label><input className="fc" value={form.label || ''} onChange={e => set('label', e.target.value)} /></div>
              <div style={{ display: 'flex', gap: 12 }}>
                <div className="fg" style={{ flex: 1 }}><label className="fl">Part</label><select className="fc" value={form.part || 1} onChange={e => set('part', parseInt(e.target.value))}><option value={1}>Part 1 (Required)</option><option value={2}>Part 2 (Optional)</option></select></div>
                <div className="fg" style={{ flex: 1 }}><label className="fl">Word count</label><input className="fc" type="number" value={form.word_count || 100} onChange={e => set('word_count', parseInt(e.target.value))} /></div>
              </div>
              <div className="fg"><label className="fl">Instruction</label><textarea className="fc fcs" rows={2} value={form.instruction || ''} onChange={e => set('instruction', e.target.value)} /></div>
              {(form.question_type || 'email') === 'email' && (
                <>
                  <div className="fg"><label className="fl">Email From</label><input className="fc fcs" value={form.email_from || ''} onChange={e => set('email_from', e.target.value)} /></div>
                  <div className="fg"><label className="fl">Email Subject</label><input className="fc fcs" value={form.email_subject || ''} onChange={e => set('email_subject', e.target.value)} /></div>
                  <div className="fg"><label className="fl">Email Body</label><textarea className="fc fcs" rows={4} value={form.email_body || ''} onChange={e => set('email_body', e.target.value)} /></div>
                  <div className="fg"><label className="fl">Notes (comma-separated)</label><input className="fc fcs" value={(form.notes || []).join(', ')} onChange={e => set('notes', e.target.value.split(',').map(n => n.trim()))} /></div>
                </>
              )}
              {(form.question_type) === 'story' && (
                <div className="fg"><label className="fl">Story Opener</label><input className="fc fcs" value={form.story_opener || ''} onChange={e => set('story_opener', e.target.value)} /></div>
              )}
              {(form.question_type) === 'article' && (
                <>
                  <div className="fg"><label className="fl">Heading</label><input className="fc fcs" value={form.prompt_heading || ''} onChange={e => set('prompt_heading', e.target.value)} /></div>
                  <div className="fg"><label className="fl">Prompt items (one per line)</label><textarea className="fc fcs" rows={3} value={(form.prompt_items || []).join('\n')} onChange={e => set('prompt_items', e.target.value.split('\n').filter(Boolean))} /></div>
                </>
              )}
            </>
          )}
          {(modal.type === 'add-speaking' || modal.type === 'edit-speaking') && (
            <>
              <div className="fg"><label className="fl">Part</label>
                <select className="fc" value={form.part || '1'} onChange={e => set('part', e.target.value)}>
                  {[['1', '1 - Interview'], ['2', '2 - Long Turn'], ['3', '3 - Collaborative'], ['4', '4 - Discussion']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div className="fg"><label className="fl">Label</label><input className="fc" value={form.label || ''} onChange={e => set('label', e.target.value)} /></div>
              <div className="fg"><label className="fl">Instruction</label><textarea className="fc fcs" rows={2} value={form.instruction || ''} onChange={e => set('instruction', e.target.value)} /></div>
              {(form.part === '1' || form.part === '4') && (
                <div className="fg"><label className="fl">Questions (one per line)</label><textarea className="fc fcs" rows={5} value={(form.questions || []).join('\n')} onChange={e => set('questions', e.target.value.split('\n').filter(Boolean))} /></div>
              )}
              {form.part === '2' && (
                <>
                  <div className="fg"><label className="fl">Situation A</label><textarea className="fc fcs" rows={2} value={form.situation_a || ''} onChange={e => set('situation_a', e.target.value)} /></div>
                  <div className="fg"><label className="fl">Situation B</label><textarea className="fc fcs" rows={2} value={form.situation_b || ''} onChange={e => set('situation_b', e.target.value)} /></div>
                </>
              )}
              {form.part === '3' && (
                <>
                  <div className="fg"><label className="fl">Central Question</label><input className="fc fcs" value={form.central_question || ''} onChange={e => set('central_question', e.target.value)} /></div>
                  <div className="fg"><label className="fl">Options (one per line)</label><textarea className="fc fcs" rows={4} value={(form.options || []).join('\n')} onChange={e => set('options', e.target.value.split('\n').filter(Boolean))} /></div>
                </>
              )}
            </>
          )}
          {modal.type === 'edit-reading' && (
            <>
              <p style={{ fontSize: 13, color: 'var(--mu)', marginBottom: 12 }}>
                Paste the JSON content for Part {form.part_number} below. See the workflow document for the exact schema for each part type.
              </p>
              <div className="fg">
                <label className="fl">Content JSON</label>
                <textarea className="fc" rows={12} style={{ fontFamily: 'Courier New', fontSize: 12 }} value={form.content_json || JSON.stringify(form.content || {}, null, 2)} onChange={e => set('content_json', e.target.value)} />
              </div>
            </>
          )}
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}
